/**
 * Página de Etiquetas (Tags) — ListView + useListViews + useStandardListFilters.
 * Misma estructura y diseño que Categorías; solo cambian datos y config.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BlockStack,
  ChoiceList,
  Modal,
  Text,
  TextField,
  IndexFiltersMode,
  useSetIndexFiltersMode,
} from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { fetchAllProductRows } from '@/lib/productsApi';
import { useAuth, usePagination } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { storePath } from '@/config/routes.config';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ListView, buildListViewPagination } from '@/components/admin/ListView';
import { getTagsListConfig } from '@/config/listViews';
import { getStatsPageCache, setStatsPageCache, listViewCacheKey } from '@/lib/statsPageCache';
import type { TagListItem } from '@/config/listViews';
import { filterBySearchName, filterByWithWithoutProducts, sortByNameOrProducts } from '@/lib/listViewFilters';
import { useListViews } from '@/hooks/useListViews';
import { useCanManage } from '@/hooks/useCanManage';
import { useStandardListFilters } from '@/hooks/useStandardListFilters';
import { exportListToExcel, getExportFilename } from '@/lib/exportToExcel';
import { ProductIcon } from '@/lib/polaris-icons';

const LIST_ID = 'tags';

const TAGS_SYSTEM_VIEWS = [
  {
    id: 'todos',
    label: 'Todos',
    isSystem: true,
    query: '',
    filterValues: { products: [] as string[] },
  },
];

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'products_desc', label: 'Más productos primero' },
  { value: 'products_asc', label: 'Menos productos primero' },
] as const;

const SORT_OPTIONS_POLARIS = SORT_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value.replace('_', ' ') as `${string} ${'asc' | 'desc'}`,
  directionLabel: o.label,
}));

export default function TagsPage() {
  const { storeSlug } = useParams<{ storeSlug?: string }>();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const canManage = useCanManage();
  const toast = useAdminToast();

  const [tags, setTags] = useState<TagListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [tagToEdit, setTagToEdit] = useState<TagListItem | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [updating, setUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<TagListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [filterProducts, setFilterProducts] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<string>(SORT_OPTIONS[0].value);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  const filtersRecord = useMemo(
    () => ({ products: filterProducts }),
    [filterProducts]
  );
  const setFiltersFromView = useCallback((f: Record<string, string[]>) => {
    setFilterProducts(f.products ?? []);
  }, []);

  const views = useListViews({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    systemViews: TAGS_SYSTEM_VIEWS,
    searchValue,
    filters: filtersRecord,
    setSearchValue,
    setFilters: setFiltersFromView,
    setMode,
    mode,
  });
  const { activeView, handleSwitchView } = views;

  const toUrl = useCallback(
    (path: string) => navigate(storeSlug ? storePath(storeSlug, path) : path),
    [navigate, storeSlug]
  );
  const config = useMemo(
    () => getTagsListConfig(toUrl, () => setAddModalOpen(true)),
    [toUrl]
  );

  const appliedFilters = useMemo(() => {
    const items: { key: string; label: string; onRemove: () => void }[] = [];
    if (filterProducts.length > 0) {
      const labels = filterProducts
        .map((v) => (v === 'with_products' ? 'Con productos' : 'Sin productos'))
        .join(', ');
      items.push({
        key: 'products',
        label: `Productos: ${labels}`,
        onRemove: () => setFilterProducts([]),
      });
    }
    return items;
  }, [filterProducts]);

  const indexFiltersFilters = useMemo(
    () => [
      {
        key: 'products',
        label: 'Productos',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Productos"
            titleHidden
            choices={[
              { label: 'Con productos', value: 'with_products' },
              { label: 'Sin productos', value: 'without_products' },
            ]}
            selected={filterProducts}
            onChange={setFilterProducts}
            allowMultiple
          />
        ),
      },
    ],
    [filterProducts]
  );

  const clearFilters = useCallback(() => {
    handleSwitchView(0);
  }, [handleSwitchView]);

  const {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode,
    editingColumnPrefs,
    onColumnOrderChange,
    onToggleColumnVisibility,
  } = useStandardListFilters({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    config,
    views,
    searchValue,
    setSearchValue,
    queryPlaceholder: activeView ? `Buscando en ${activeView.label}` : 'Buscar etiquetas',
    filters: indexFiltersFilters,
    appliedFilters,
    onClearAll: clearFilters,
    sortOptions: SORT_OPTIONS_POLARIS,
    sortSelected: [sortOption.replace('_', ' ')],
    onSort: (v) => setSortOption((v[0] ?? sortOption).replace(' ', '_')),
    mode,
    setMode,
  });

  const loadTags = useCallback(async (revalidateOnly = false) => {
    if (!tenantId) return;
    const cacheKey = listViewCacheKey('tags', tenantId);
    if (!revalidateOnly) {
      const cached = getStatsPageCache<{ items: TagListItem[] }>(cacheKey);
      if (cached?.items !== undefined) {
        setTags(cached.items);
        setLoading(false);
        loadTags(true).catch(() => {});
        return;
      }
    }
    if (!revalidateOnly) setLoading(true);
    try {
      // `products` se pagina: sin eso PostgREST cortaba en 1000 y los product_count por
      // etiqueta sub-contaban en silencio pasando esa marca.
      const [tagsResult, allProducts] = await Promise.all([
        supabase
          .from('product_tags')
          .select('id, name, tenant_id, created_at')
          .eq('tenant_id', tenantId)
          .order('name'),
        fetchAllProductRows<{ tags?: string[] | string | null }>(tenantId, 'tags'),
      ]);

      if (tagsResult.error) throw tagsResult.error;

      const productCountByTagName: Record<string, number> = {};
      allProducts.forEach((product: { tags?: string[] | string | null }) => {
        let tagNames: string[] = [];
        if (Array.isArray(product.tags)) {
          tagNames = product.tags;
        } else if (typeof product.tags === 'string') {
          try {
            const parsed = JSON.parse(product.tags);
            tagNames = Array.isArray(parsed) ? parsed : [];
          } catch {
            tagNames = [];
          }
        }
        tagNames.forEach((tagName: string) => {
          if (tagName) {
            productCountByTagName[tagName] = (productCountByTagName[tagName] || 0) + 1;
          }
        });
      });

      const tagsWithCounts = (tagsResult.data || []).map((tag) => ({
        ...tag,
        product_count: productCountByTagName[tag.name] || 0,
      })) as TagListItem[];

      setTags(tagsWithCounts);
      setStatsPageCache(cacheKey, { items: tagsWithCounts });
    } catch (error: unknown) {
      toast.error('Error al cargar etiquetas', error instanceof Error ? error.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [tenantId, toast]);

  useLayoutEffect(() => {
    if (tenantId) loadTags();
  }, [tenantId, loadTags]);

  const handleAddTag = useCallback(async () => {
    if (!tenantId || !newTagName.trim()) {
      toast.error('El nombre de la etiqueta es requerido');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('product_tags').insert({
        tenant_id: tenantId,
        name: newTagName.trim(),
      });
      if (error) throw error;
      toast.success('Etiqueta creada', newTagName.trim());
      setAddModalOpen(false);
      setNewTagName('');
      await loadTags();
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      if (err?.code === '23505') {
        toast.error('Ya existe una etiqueta con ese nombre');
      } else {
        toast.error('Error al crear etiqueta', err?.message ?? 'Error desconocido');
      }
    } finally {
      setSaving(false);
    }
  }, [tenantId, newTagName, loadTags, toast]);

  const handleEditClick = useCallback((tag: TagListItem) => {
    setTagToEdit(tag);
    setEditTagName(tag.name);
    setEditModalOpen(true);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!tenantId || !tagToEdit || !editTagName.trim()) return;
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('product_tags')
        .update({ name: editTagName.trim() })
        .eq('id', tagToEdit.id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
      toast.success('Etiqueta actualizada', editTagName.trim());
      setEditModalOpen(false);
      setTagToEdit(null);
      setEditTagName('');
      await loadTags();
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      if (err?.code === '23505') {
        toast.error('Ya existe una etiqueta con ese nombre');
      } else {
        toast.error('Error al actualizar etiqueta', err?.message ?? 'Error desconocido');
      }
    } finally {
      setUpdating(false);
    }
  }, [tenantId, tagToEdit, editTagName, loadTags, toast]);

  const handleDeleteClick = useCallback((tag: TagListItem) => {
    setTagToDelete(tag);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!tagToDelete) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from('product_tags').delete().eq('id', tagToDelete.id);
      if (error) throw error;
      toast.success('Etiqueta eliminada', tagToDelete.name);
      setDeleteModalOpen(false);
      setTagToDelete(null);
      await loadTags();
    } catch (error: unknown) {
      toast.error('Error al eliminar etiqueta', error instanceof Error ? error.message : 'Error desconocido');
    } finally {
      setDeleting(false);
    }
  }, [tagToDelete, loadTags, toast]);

  // Borrado masivo: la plantilla pone la acción destructiva, el modal y el toast; acá solo el borrado real.
  const bulkDeleteTags = useCallback(async (ids: string[]) => {
    if (!tenantId) return 0;
    const { error } = await supabase.from('product_tags').delete().in('id', ids).eq('tenant_id', tenantId);
    if (error) throw error;
    await loadTags();
    return ids.length;
  }, [tenantId, loadTags]);

  const filteredTags = useMemo(() => {
    const bySearch = filterBySearchName(tags, searchValue);
    const byProducts = filterByWithWithoutProducts(bySearch, filterProducts);
    return sortByNameOrProducts(byProducts, sortOption);
  }, [tags, searchValue, filterProducts, sortOption]);

  const pagination = usePagination();
  useEffect(() => {
    pagination.setTotal(filteredTags.length);
    if (filteredTags.length > 0 && pagination.offset >= filteredTags.length) {
      pagination.goToFirstPage();
    }
  }, [filteredTags.length, pagination]);

  const listItems = useMemo(
    () => filteredTags.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [filteredTags, pagination.offset, pagination.pageSize]
  );

  const handleBulkAction = useCallback(
    (actionKey: string, selectedIds: string[]) => {
      if (actionKey === 'export' && selectedIds.length > 0) {
        const items = filteredTags.filter((t) => selectedIds.includes(t.id));
        exportListToExcel({
          columns: config.columns,
          items: items.map((t): Record<string, unknown> => ({ ...t })),
          filename: getExportFilename(config.title),
        });
        toast.success('Exportado', `${items.length} etiqueta(s) exportada(s) a Excel`);
      }
    },
    [config.columns, config.title, filteredTags, toast]
  );

  const paginationOption = buildListViewPagination(pagination);

  const pageTitleNode = (
    <PageBreadcrumbTitle
      icon={ProductIcon}
      title={config.title}
      onBack={config.onBack}
      backAccessibilityLabel={config.backAccessibilityLabel ?? 'Volver a Productos'}
    />
  );

  return (
    <>
      <ListView<TagListItem>
        config={config}
        items={listItems}
        loading={loading}
        pagination={paginationOption}
        loadingMessage="Cargando etiquetas…"
        pageTitle={pageTitleNode}
        contentBefore={filterControl}
        contentBeforeInsideCard
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        bulkDelete={{ onDelete: bulkDeleteTags, can: canManage }}
        columnOrder={tableColumnOrder}
        hiddenColumnKeys={tableHiddenKeys}
        editColumnsMode={editColumnsMode}
        columnOrderForEdit={editingColumnPrefs?.columnOrder?.length ? editingColumnPrefs.columnOrder : tableColumnOrder}
        onColumnOrderChange={onColumnOrderChange}
        onToggleColumnVisibility={onToggleColumnVisibility}
        onRowEdit={handleEditClick}
        onRowDelete={handleDeleteClick}
        onBulkAction={handleBulkAction}
      />

      <Modal
        open={addModalOpen}
        onClose={() => !saving && (setAddModalOpen(false), setNewTagName(''))}
        title="Agregar etiqueta"
        primaryAction={{
          content: 'Agregar',
          onAction: handleAddTag,
          disabled: saving || !newTagName.trim(),
          loading: saving,
        }}
        secondaryActions={[
          { content: 'Cancelar', onAction: () => (setAddModalOpen(false), setNewTagName('')), disabled: saving },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Nombre"
              value={newTagName}
              onChange={setNewTagName}
              placeholder="ej: Oferta, Nuevo, Vegetariano…"
              autoComplete="off"
              requiredIndicator
              autoFocus
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={editModalOpen}
        onClose={() => !updating && (setEditModalOpen(false), setTagToEdit(null), setEditTagName(''))}
        title="Editar etiqueta"
        primaryAction={{
          content: 'Guardar',
          onAction: handleEditSave,
          disabled: updating || !editTagName.trim() || (tagToEdit && editTagName.trim() === tagToEdit.name),
          loading: updating,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: () => (setEditModalOpen(false), setTagToEdit(null), setEditTagName('')),
            disabled: updating,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Nombre"
              value={editTagName}
              onChange={setEditTagName}
              placeholder="ej: Oferta, Nuevo, Vegetariano…"
              autoComplete="off"
              requiredIndicator
              autoFocus
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => !deleting && (setDeleteModalOpen(false), setTagToDelete(null))}
        title="Eliminar etiqueta"
        primaryAction={{
          content: deleting ? 'Eliminando…' : 'Eliminar',
          tone: 'critical',
          onAction: handleDeleteConfirm,
          loading: deleting,
        }}
        secondaryActions={[
          {
            content: 'Cancelar',
            onAction: () => (setDeleteModalOpen(false), setTagToDelete(null)),
            disabled: deleting,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd">
              ¿Estás seguro de que deseas eliminar la etiqueta "{tagToDelete?.name}"? Esta acción no se puede deshacer.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

    </>
  );
}
